import React, { FC } from 'react';
import { formatDate } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CardActions, CardContent, Typography } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { Deviation, DeviationAction } from 'models/deviations';
import CardInformation from 'components/shared/card-information/card-information.component';
import DeviationActionListItem from 'components/order-details/main-order-view/deviation-section/deviation-action/deviation-action-list-item/deviation-action-list-item.component';

type DeviationItemProps = {
    deviation: Deviation;
    onOpenUpdateModal: (deviation: Deviation) => void;
    onOpenConfirmDialog: (deviationId: string) => void;
    onActionDialogOpen: (deviationId: string) => void;
    onOpenUpdateActionModal: (deviationId: string, deviationAction: DeviationAction) => void;
    onOpenConfirmActionDialog: (deviationId: string, actionId: string) => void;
    versionCheck: boolean;
};

const DeviationItem: FC<DeviationItemProps> = ({
    deviation,
    onOpenUpdateModal,
    onOpenConfirmDialog,
    onActionDialogOpen,
    onOpenUpdateActionModal,
    onOpenConfirmActionDialog,
    versionCheck,
}: DeviationItemProps) => {
    const { t: translate } = useTranslation(['deviations']);

    const deviationTitle = (deviation: Deviation) => {
        // DD-3494 rename: new field first, deprecated field as fallback until backfill.
        return deviation.visibleForCustomer ?? deviation.visibleForDentist
            ? `${translate(deviation.type)}: ${deviation.code} - ${deviation.description} (${translate(
                  'deviations:VISIBLE_FOR_CUSTOMER',
              )})`
            : `${translate(deviation.type)}: ${deviation.code} - ${deviation.description}`;
    };

    const subheader = (deviation: Deviation) => {
        return `${formatDate(deviation.addedOn, 'datetime')} ${translate('deviations:BY')} ${deviation.technician}`;
    };

    const handleEditDeviationModal = (deviation: Deviation) => {
        onOpenUpdateModal(deviation);
    };

    const handleEditDeviationActionModal = (deviationAction: DeviationAction) => {
        onOpenUpdateActionModal(deviation._id as string, deviationAction);
    };

    const handleDeleteDeviationModal = (deviationId: string) => {
        onOpenConfirmDialog(deviationId);
    };

    const handleDeleteDeviationActionModal = (actionId: string) => {
        onOpenConfirmActionDialog(deviation._id as string, actionId);
    };

    return (
        <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#fdfdfd' }}>
            <CardContent>
                <Typography variant="h2" mb={2}>
                    {translate('deviations:DEVIATION')}
                </Typography>
                <CardInformation
                    icon={<WarningIcon color="warning" />}
                    title={deviationTitle(deviation)}
                    subHeader={subheader(deviation)}
                    content={deviation.report}
                    deviation={deviation}
                    onOpenUpdateModal={handleEditDeviationModal}
                    onOpenConfirmDialog={handleDeleteDeviationModal}
                    versionCheck={versionCheck}
                    dataTest="deviation-list-item"
                />
                {deviation.actions?.map((action) => (
                    <Box key={action._id}>
                        <DeviationActionListItem
                            deviationAction={action}
                            onOpenUpdateModal={handleEditDeviationActionModal}
                            onOpenConfirmDialog={handleDeleteDeviationActionModal}
                            versionCheck={versionCheck}
                        />
                    </Box>
                ))}
                {versionCheck ? (
                    <CardActions>
                        <Box display="flex" flexGrow={1} justifyContent="center">
                            <Button
                                onClick={() => onActionDialogOpen(deviation._id as string)}
                                data-test="deviation-item.add-action-btn"
                            >
                                {translate('deviations:ADD_ACTION')}
                            </Button>
                        </Box>
                    </CardActions>
                ) : undefined}
            </CardContent>
        </Card>
    );
};

export default DeviationItem;
